import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/MonthlyProgressTracker.css';

const PLAN_CONFIG = [
  { key: 'blog_post', label: 'Blog Posts' },
  { key: 'social_media', label: 'Social Posts' }
];

// Polling interval while this tracker is mounted - catches approvals this
// operator didn't trigger themselves (a client approving from their own
// dashboard, or a Yellow Light auto-approval firing server-side) without
// requiring a manual page refresh.
const POLL_INTERVAL_MS = 20000;

// Shows an operator-facing summary of how many posts have been client-approved
// this month against the brand's subscription quota. Sending an idea or draft
// to the client is not progress here - only client approval counts.
// refreshKey: bump it from the parent (e.g. right after a Green Light
// instant-approval the operator just triggered) to force an immediate
// re-fetch instead of waiting for the next poll.
const MonthlyProgressTracker = ({ brandId, refreshKey }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brandId) {
      setProgress(null);
      return;
    }

    let cancelled = false;

    const fetchProgress = (showLoading) => {
      if (showLoading) setLoading(true);
      axios.get(`/api/brands/${brandId}/monthly-progress`, { withCredentials: true })
        .then(response => {
          if (!cancelled) setProgress(response.data);
        })
        .catch(() => {
          if (!cancelled) setProgress(null);
        })
        .finally(() => {
          if (!cancelled && showLoading) setLoading(false);
        });
    };

    fetchProgress(true);
    const interval = setInterval(() => fetchProgress(false), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [brandId, refreshKey]);

  if (!brandId || loading || !progress) return null;

  const activePlans = PLAN_CONFIG
    .map(plan => ({ ...plan, data: progress[plan.key] }))
    .filter(plan => plan.data && plan.data.enabled && plan.data.quota > 0);

  if (activePlans.length === 0) return null;

  const monthLabel = new Date(`${progress.month}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  });

  return (
    <div className="mpt-tracker">
      <span className="mpt-tracker-title">{monthLabel} Progress</span>
      <div className="mpt-items">
        {activePlans.map(({ key, label, data }) => {
          const pct = Math.min(100, Math.round((data.approved / data.quota) * 100));
          const metQuota = data.approved >= data.quota;
          return (
            <div className="mpt-item" key={key}>
              <div className="mpt-item-row">
                <span className="mpt-item-label">{label}</span>
                <span className={`mpt-item-count ${metQuota ? 'mpt-count-met' : ''}`}>
                  {data.approved}/{data.quota} approved
                </span>
              </div>
              <div className="mpt-bar-track">
                <div
                  className={`mpt-bar-fill ${metQuota ? 'mpt-bar-fill-met' : ''}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {data.in_review > 0 && (
                <span className="mpt-item-subtext">{data.in_review} awaiting client approval</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyProgressTracker;
